import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SwipeToAction from '@/components/SwipeToAction';
import { notifyWithSoundAndVibration, initAudioContext } from '@/utils/notificationSound';
import usePlatformFee from '@/hooks/usePlatformFee';
import { 
  Calendar, Clock, User, Car, Bell, CheckCircle2, XCircle, 
  Loader2, RefreshCw, MapPin, Play, Square, DollarSign, 
  KeyRound, AlertCircle, TrendingUp, History,
  ChevronRight, Banknote, CalendarDays, X, Settings,
  CreditCard, AlertTriangle, ArrowLeft, Timer, MapPinned,
  Volume2, Lock
} from 'lucide-react';

const REFRESH_INTERVAL = 10000;

// Componente de Timer de Reserva do Slot
const SlotReservationTimer = ({ slotExpiresAt, slotReservedAt, onExpire }) => {
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    if (!slotReservedAt && !slotExpiresAt) {
      setTimeRemaining(null);
      return;
    }

    const calculateRemaining = () => {
      let expiryTime;
      
      if (slotExpiresAt) {
        expiryTime = new Date(slotExpiresAt);
      } else if (slotReservedAt) {
        // Se não tem slot_expires_at, calcular baseado em 10 minutos após slot_reserved_at
        expiryTime = new Date(new Date(slotReservedAt).getTime() + 10 * 60 * 1000);
      }
      
      const now = new Date();
      const diff = Math.max(0, Math.floor((expiryTime - now) / 1000));
      
      if (diff <= 0) {
        setIsExpired(true);
        setTimeRemaining(0);
        if (onExpire) onExpire();
        return 0;
      }
      
      setTimeRemaining(diff);
      return diff;
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 1000);
    
    return () => clearInterval(interval);
  }, [slotExpiresAt, slotReservedAt, onExpire]);

  if (timeRemaining === null) return null;
  
  if (isExpired) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
        <AlertCircle className="w-4 h-4" />
        <span>Expirado</span>
      </div>
    );
  }

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  const isUrgent = timeRemaining < 60;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
      isUrgent ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-orange-100 text-orange-700'
    }`}>
      <Timer className="w-4 h-4" />
      <span>
        {minutes > 0 ? `${minutes}m ` : ''}{seconds.toString().padStart(2, '0')}s
      </span>
    </div>
  );
};

export default function ProviderServices() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  // SOURCE OF TRUTH dinâmico para comissão (admin → Financeiro → Configurações)
  const { providerNetRatio, platformFeePercent } = usePlatformFee();
  
  const [pendingAppointments, setPendingAppointments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loadingPending, setLoadingPending] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  
  const [acceptedAppointments, setAcceptedAppointments] = useState([]);
  const [inProgressAppointments, setInProgressAppointments] = useState([]);
  const [awaitingCodeAppointments, setAwaitingCodeAppointments] = useState([]);
  const [completedAppointments, setCompletedAppointments] = useState([]);
  const [expiredAppointments, setExpiredAppointments] = useState([]); // SAFE PATCH: Expirados
  const [locallyExpiredIds, setLocallyExpiredIds] = useState([]); // Problem 2: reservas vencidas (10min) detectadas localmente
  const [allAppointments, setAllAppointments] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  
  // Modal de código
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  
  // Modal de detalhes do serviço
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedServiceDetails, setSelectedServiceDetails] = useState(null);
  
  // Estado do Pix
  const [hasPixConfigured, setHasPixConfigured] = useState(true); // Assume true até verificar
  
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [authChecked, setAuthChecked] = useState(false);
  // Problem 4: guarda persistente entre re-renders/polling — cada solicitação (por id) dispara o toast de expiração no máximo 1x
  const shownExpiryToastIds = useRef(new Set());
  
  // ==================== SAFE PATCH: Check-in Notification ====================
  // Rastrear IDs de clientes que já fizeram check-in para evitar notificações repetidas
  const checkedInClientsRef = useRef(new Set());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioInitializedRef = useRef(false);
  
  // Inicializar contexto de áudio na primeira interação
  const handleFirstInteraction = useCallback(() => {
    if (!audioInitializedRef.current) {
      initAudioContext();
      audioInitializedRef.current = true;
      // Remover listener após primeira interação
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    }
  }, []);
  
  useEffect(() => {
    // Adicionar listener para inicializar áudio na primeira interação
    document.addEventListener('click', handleFirstInteraction);
    document.addEventListener('touchstart', handleFirstInteraction);
    
    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('touchstart', handleFirstInteraction);
    };
  }, [handleFirstInteraction]);
  
  // Função para verificar novos check-ins e notificar
  const checkForNewCheckins = useCallback((appointments) => {
    if (!soundEnabled) return;
    
    const currentCheckedIn = appointments.filter(a => 
      a.client_checked_in === true && a.status === 'ACCEPTED'
    );
    
    currentCheckedIn.forEach(appointment => {
      const appointmentId = appointment.id;
      
      // Se é um novo check-in (não estava na lista anterior)
      if (!checkedInClientsRef.current.has(appointmentId)) {
        checkedInClientsRef.current.add(appointmentId);
        
        // Tocar som e vibrar
        notifyWithSoundAndVibration('checkin', [100, 50, 100, 50, 100]);
        
        // Mostrar toast especial
        toast.success(
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-full">
              <MapPinned className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-green-900">🔔 Cliente chegou ao lava-jato!</p>
              <p className="text-sm text-green-700 mt-0.5">
                {appointment.client_name} já está aguardando.
              </p>
              <p className="text-xs text-green-600 mt-1">
                Serviço: {appointment.time} - {appointment.service_type}
              </p>
            </div>
          </div>,
          {
            duration: 8000,
            className: 'checkin-notification',
            style: {
              background: 'linear-gradient(to right, #ecfdf5, #d1fae5)',
              border: '2px solid #10b981',
              borderRadius: '12px'
            }
          }
        );
      }
    });
  }, [soundEnabled]);
  // ==================== FIM SAFE PATCH ====================

  useEffect(() => {
    // Verificar se há token antes de redirecionar
    const token = localStorage.getItem('token');
    
    // Se não há token, redirecionar imediatamente
    if (!token) {
      navigate('/provider/login');
      return;
    }
    
    // Se há token mas user ainda não carregou, aguardar
    if (!user) {
      return;
    }
    
    // User carregou, verificar se é provider
    setAuthChecked(true);
    if (user.user_type !== 'provider') {
      navigate('/provider/login');
      return;
    }
    
    fetchAll();
    fetchPixStatus();
    const interval = setInterval(() => fetchAll(true), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [user, navigate]);

  const fetchPixStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/provider/payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasPixConfigured(res.data.has_payout && res.data.payout?.is_active);
    } catch (error) {
      setHasPixConfigured(false);
    }
  };

  const fetchAll = useCallback(async (silent = false) => {
    await Promise.all([
      fetchPendingAppointments(silent),
      fetchAllAppointments(silent)
    ]);
    setLastRefresh(new Date());
  }, []);

  const fetchPendingAppointments = async (silent = false) => {
    if (!silent) setLoadingPending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/provider/appointments?status=PENDING`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pending = res.data.appointments || [];
      setPendingAppointments(pending);
      setPendingCount(pending.length);
    } catch (error) {
      if (!silent) console.error(error);
    } finally {
      setLoadingPending(false);
    }
  };

  const fetchAllAppointments = async (silent = false) => {
    if (!silent) setLoadingServices(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/provider/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const appointments = res.data.appointments || [];
      setAllAppointments(appointments);
      
      // SAFE PATCH: Verificar novos check-ins e notificar
      checkForNewCheckins(appointments);
      
      setAcceptedAppointments(appointments.filter(a => a.status === 'ACCEPTED'));
      setInProgressAppointments(appointments.filter(a => a.status === 'IN_PROGRESS'));
      setAwaitingCodeAppointments(appointments.filter(a => a.status === 'AWAITING_CODE'));
      setCompletedAppointments(appointments.filter(a => ['COMPLETED', 'PAID'].includes(a.status)));
      setExpiredAppointments(appointments.filter(a => a.status === 'EXPIRED')); // SAFE PATCH
    } catch (error) {
      if (!silent) console.error(error);
    } finally {
      setLoadingServices(false);
    }
  };

  // Cálculos para resumo
  const today = new Date().toISOString().split('T')[0];
  
  const todayAppointments = allAppointments.filter(a => a.date === today);
  const todayCompleted = todayAppointments.filter(a => ['COMPLETED', 'PAID'].includes(a.status));
  const todayCancelled = allAppointments.filter(a => a.date === today && a.status === 'CANCELLED');
  
  // Problem 7: "Próximo serviço agendado" representa SOMENTE serviços efetivamente ACEITOS.
  // (Pendente/Recusado/Expirado/Cancelado/Concluído não têm status ACCEPTED → não entram aqui.)
  const nextScheduled = [...acceptedAppointments]
    .sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA - dateB;
    })[0];

  // SOURCE-OF-TRUTH ÚNICO: backend grava appointment.price com base em
  // db.plans → services[].price_per_unit. Sem fallback hardcoded.
  const getAppointmentGrossPrice = (app) =>
    Number(app?.price ?? app?.total_price ?? app?.service_price ?? 0) || 0;

  // Cálculos financeiros (faturamento bruto — TODOS os serviços concluídos,
  // inclusive contestados; o histórico operacional deve permanecer).
  const calculateEarnings = (appointments) => {
    return appointments.reduce((total, app) => total + getAppointmentGrossPrice(app), 0);
  };

  // Filtro para indicadores LÍQUIDOS (Disponível): exclui serviços em análise
  // e revertidos por contestação — o histórico segue visível, mas o valor
  // líquido NÃO entra no "disponível para repasse".
  const isNetEligible = (app) => {
    const fs = app?.financial_status;
    return fs !== 'em_analise' && fs !== 'revertido' && fs !== 'bloqueado';
  };

  const todayEarnings = calculateEarnings(todayCompleted);
  
  // Ganhos do mês (filtrar por mês atual)
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const monthlyCompleted = completedAppointments.filter(a => {
    const appDate = new Date(a.date);
    return appDate.getMonth() === currentMonth && appDate.getFullYear() === currentYear;
  });
  const monthlyEarnings = calculateEarnings(monthlyCompleted);

  // SOURCE OF TRUTH dinâmico — comissão da plataforma vinda do painel admin.
  // Disponível (líquido) usa APENAS appointments elegíveis (não contestados).
  const NET_RATIO = providerNetRatio;
  const todayNetEarnings = calculateEarnings(todayCompleted.filter(isNetEligible)) * NET_RATIO;
  const monthlyNetEarnings = calculateEarnings(monthlyCompleted.filter(isNetEligible)) * NET_RATIO;

  // Histórico cancelados
  const cancelledAppointments = allAppointments.filter(a => 
    ['CANCELLED', 'REJECTED'].includes(a.status)
  );

  // ==================== PROBLEM 2: EXPIRAÇÃO LOCAL DA SOLICITAÇÃO (10 min) ====================
  // Uma solicitação PENDING_PROVIDER_ACCEPTANCE/PENDING cujo prazo de 10 min (a partir de
  // slot_reserved_at) terminou deve sair IMEDIATAMENTE das pendentes e aparecer em
  // "Serviços Expirados", mesmo antes de o backend persistir o status EXPIRED.
  const SLOT_WINDOW_MS = 10 * 60 * 1000;
  const isSlotLocallyExpired = (a) => {
    const isPending = a.status === 'PENDING' || a.status === 'PENDING_PROVIDER_ACCEPTANCE';
    if (!isPending || !a.slot_reserved_at) return false;
    return Date.now() >= new Date(a.slot_reserved_at).getTime() + SLOT_WINDOW_MS;
  };
  const isPendingExpired = (a) => isSlotLocallyExpired(a) || locallyExpiredIds.includes(a.id);

  // Pendentes visíveis (mata o flash: exclui as já vencidas)
  const visiblePendingAppointments = pendingAppointments.filter(a => !isPendingExpired(a));
  // Expirados exibidos: EXPIRED do backend + vencidas localmente (sem duplicar por id)
  const locallyExpiredPending = pendingAppointments.filter(isPendingExpired);
  const displayExpiredAppointments = [
    ...expiredAppointments,
    ...locallyExpiredPending.filter(a => !expiredAppointments.some(e => e.id === a.id)),
  ];
  // ==================== FIM PROBLEM 2 ====================

  const handleDecision = async (appointmentId, decision) => {
    if (decision === 'reject' && !confirm('Deseja realmente recusar esta solicitação?')) return;

    setActionLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      const endpoint = decision === 'accept' 
        ? `${API}/appointments/${appointmentId}/accept`
        : `${API}/appointments/${appointmentId}/reject`;
      
      await axios.post(
        endpoint,
        decision === 'reject' ? { reason: 'Recusado pelo prestador' } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(decision === 'accept' ? 'Agendamento confirmado!' : 'Agendamento recusado');
      
      setPendingAppointments(prev => prev.filter(a => a.id !== appointmentId));
      setPendingCount(prev => Math.max(0, prev - 1));
      setTimeout(() => fetchAll(true), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar decisão');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartService = async (appointmentId) => {
    setActionLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/appointments/${appointmentId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Serviço iniciado!');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar serviço');
    } finally {
      setActionLoading(null);
    }
  };

  const handleFinishService = async (appointmentId) => {
    if (!confirm('Confirmar finalização do serviço?')) return;
    
    setActionLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/appointments/${appointmentId}/finish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message || 'Serviço finalizado! Solicite o código ao cliente.');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar serviço');
    } finally {
      setActionLoading(null);
    }
  };

  const openCodeModal = (appointment) => {
    setSelectedAppointment(appointment);
    setCodeInput('');
    setCodeModalOpen(true);
  };

  const handleConfirmCode = async () => {
    if (!codeInput || codeInput.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setCodeLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/appointments/${selectedAppointment.id}/confirm-code`,
        { code: codeInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(res.data.message || 'Código confirmado! Serviço concluído.');
      setCodeModalOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setCodeLoading(false);
    }
  };

  // Abrir modal de detalhes
  const openDetailsModal = (appointment) => {
    setSelectedServiceDetails(appointment);
    setDetailsModalOpen(true);
  };

  // Função para obter label do status
  const getStatusLabel = (status) => {
    const labels = {
      'PENDING': 'Aguardando resposta',
      'PENDING_PROVIDER_ACCEPTANCE': 'Aguardando sua resposta',
      'PENDING_PROVIDER_CONFIRMATION': 'Aguardando confirmação',
      'ACCEPTED': 'Confirmado',
      'IN_PROGRESS': 'Em andamento',
      'AWAITING_CODE': 'Aguardando código',
      'COMPLETED': 'Concluído',
      'PAID': 'Pago',
      'CANCELLED': 'Cancelado',
      'REJECTED': 'Recusado',
      'EXPIRED': 'Expirado',
      'CANCELED_NO_CHECKIN': 'Cancelado (sem check-in)',
      'CANCELED_PROVIDER_DELAY': 'Cancelado (atraso prestador)'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDING': 'bg-yellow-100 text-yellow-800',
      'PENDING_PROVIDER_ACCEPTANCE': 'bg-orange-100 text-orange-800',
      'PENDING_PROVIDER_CONFIRMATION': 'bg-yellow-100 text-yellow-800',
      'ACCEPTED': 'bg-green-100 text-green-800',
      'IN_PROGRESS': 'bg-blue-100 text-blue-800',
      'AWAITING_CODE': 'bg-orange-100 text-orange-800',
      'COMPLETED': 'bg-emerald-100 text-emerald-800',
      'PAID': 'bg-emerald-100 text-emerald-800',
      'CANCELLED': 'bg-red-100 text-red-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'EXPIRED': 'bg-gray-100 text-gray-800',
      'CANCELED_NO_CHECKIN': 'bg-red-100 text-red-800',
      'CANCELED_PROVIDER_DELAY': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Função para formatar tipo de serviço
  const formatServiceType = (type) => {
    const types = {
      'complete': 'Lavagem Completa',
      'lavagem_completa': 'Lavagem Completa',
      'ducha': 'Ducha Simples',
      'ducha_simples': 'Ducha Simples'
    };
    return types[type?.toLowerCase()] || type || 'Lavagem';
  };

  // Renderização dos cards (com clique para detalhes)
  const renderPendingCard = (appointment) => {
    const isLoading = actionLoading === appointment.id;
    const providerAmount = appointment.provider_amount || (appointment.price ? appointment.price * NET_RATIO : 0);
    
    // Handler para quando o slot expirar
    const handleSlotExpire = () => {
      // Problem 4: dedup por appointment.id (id estável e único) → 1 toast por solicitação,
      // sobrevive a re-renders/polling porque o Set vive em useRef (não é recriado a cada render).
      if (!shownExpiryToastIds.current.has(appointment.id)) {
        shownExpiryToastIds.current.add(appointment.id);
        toast.warning(`A solicitação de ${appointment.client_name || 'cliente'} expirou.`);
      }
      // Problem 2: marca a solicitação como expirada localmente → sai das pendentes
      // (Aceitar/Recusar somem) e passa a aparecer em "Serviços Expirados" no mesmo instante.
      setLocallyExpiredIds(prev => (prev.includes(appointment.id) ? prev : [...prev, appointment.id]));
    };
    
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-2 border-yellow-400 bg-gradient-to-r from-yellow-50 to-amber-50 cursor-pointer hover:shadow-lg transition-all"
        onClick={() => openDetailsModal(appointment)}
        data-testid={`pending-card-${appointment.id}`}
      >
        {/* Header com Badge e Timer */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Badge className="bg-yellow-500 text-white px-3 py-1">
              <Bell className="w-3 h-3 mr-1" />
              Nova Solicitação
            </Badge>
            {/* Timer de Reserva do Slot */}
            <SlotReservationTimer 
              slotExpiresAt={appointment.slot_expires_at}
              slotReservedAt={appointment.slot_reserved_at}
              onExpire={handleSlotExpire}
            />
          </div>
          <span className="text-xs text-muted-foreground">
            {new Date(appointment.created_at).toLocaleString('pt-BR', { 
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
            })}
          </span>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          {/* Informações Principais */}
          <div className="flex-1 space-y-4">
            {/* Cliente */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-full shadow-sm">
                <User className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Cliente</p>
                <p className="font-semibold text-lg">{appointment.client_name || 'Cliente'}</p>
              </div>
            </div>
            
            {/* Veículo */}
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-full shadow-sm">
                <Car className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Veículo</p>
                <p className="font-medium">
                  {appointment.vehicle_brand || 'N/A'} {appointment.vehicle_model || ''}
                  {appointment.vehicle_plate && (
                    <Badge variant="outline" className="ml-2 font-mono text-xs">
                      {appointment.vehicle_plate}
                    </Badge>
                  )}
                </p>
              </div>
            </div>

            {/* Grid de informações */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-white/70 rounded-lg">
              {/* Tipo de Serviço */}
              <div className="text-center p-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Serviço</p>
                <Badge className="mt-1 bg-blue-100 text-blue-800 border-blue-300">
                  {formatServiceType(appointment.service_type)}
                </Badge>
              </div>
              
              {/* Valor a Receber */}
              <div className="text-center p-2 bg-emerald-50 rounded-lg">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Você Recebe</p>
                <p className="font-bold text-lg text-emerald-600">
                  R$ {providerAmount.toFixed(2)}
                </p>
              </div>
              
              {/* Data */}
              <div className="text-center p-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Data</p>
                <p className="font-semibold flex items-center justify-center gap-1">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit'
                  })}
                </p>
              </div>
              
              {/* Horário */}
              <div className="text-center p-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Horário</p>
                <p className="font-semibold flex items-center justify-center gap-1">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  {appointment.time}
                </p>
              </div>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex flex-row lg:flex-col gap-2 lg:min-w-[140px]" onClick={(e) => e.stopPropagation()}>
            <Button
              onClick={() => handleDecision(appointment.id, 'accept')}
              disabled={isLoading}
              className="flex-1 bg-green-600 hover:bg-green-700 h-12 text-base"
              data-testid={`accept-btn-${appointment.id}`}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><CheckCircle2 className="w-5 h-5 mr-2" />Aceitar</>
              )}
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleDecision(appointment.id, 'reject')}
              disabled={isLoading}
              className="flex-1 h-12 text-base"
              data-testid={`reject-btn-${appointment.id}`}
            >
              <XCircle className="w-5 h-5 mr-2" />Recusar
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  const renderAcceptedCard = (appointment) => {
    const isLoading = actionLoading === appointment.id;
    const clientCheckedIn = appointment.client_checked_in;
    
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-l-4 border-l-green-400 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => openDetailsModal(appointment)}
        data-testid={`accepted-card-${appointment.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <User className="w-5 h-5 text-slate-600" />
              <p className="font-semibold">{appointment.client_name}</p>
              <Badge className="bg-green-100 text-green-800">Confirmado</Badge>
              {/* Badge de check-in do cliente */}
              {clientCheckedIn && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1">
                  <MapPinned className="w-3 h-3" />
                  Cliente fez check-in
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span>{appointment.vehicle_brand} {appointment.vehicle_model}</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs w-fit">{appointment.vehicle_plate}</Badge>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>{new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>{appointment.time}</span>
              </div>
            </div>
          </div>

          {/* Swipe to Start - Bloqueado até cliente fazer check-in */}
          <div className="w-full md:w-64" onClick={(e) => e.stopPropagation()}>
            {clientCheckedIn ? (
              <SwipeToAction
                onSwipeComplete={() => handleStartService(appointment.id)}
                label="Deslize para iniciar"
                icon={Play}
                disabled={isLoading}
                loading={isLoading}
                trackColor="bg-blue-100"
                thumbColor="bg-blue-600"
                completeColor="bg-green-500"
                data-testid={`swipe-start-${appointment.id}`}
              />
            ) : (
              <div 
                className="flex items-center justify-center gap-2 py-3 px-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700"
                data-testid={`waiting-checkin-${appointment.id}`}
              >
                <Lock className="w-4 h-4" />
                <span className="text-sm font-medium">Aguardando cliente chegar</span>
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  const renderInProgressCard = (appointment) => {
    const isLoading = actionLoading === appointment.id;
    
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-l-4 border-l-blue-400 bg-blue-50/30 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => openDetailsModal(appointment)}
        data-testid={`inprogress-card-${appointment.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              <p className="font-semibold">{appointment.client_name}</p>
              <Badge className="bg-blue-100 text-blue-800">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />Em Andamento
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span>{appointment.vehicle_brand} {appointment.vehicle_model}</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs w-fit">{appointment.vehicle_plate}</Badge>
            </div>
          </div>

          <Button
            onClick={(e) => { e.stopPropagation(); handleFinishService(appointment.id); }}
            disabled={isLoading}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid={`finish-btn-${appointment.id}`}
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><Square className="w-4 h-4 mr-2" />Finalizar Serviço</>
            )}
          </Button>
        </div>
      </Card>
    );
  };

  const renderAwaitingCodeCard = (appointment) => {
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-l-4 border-l-yellow-500 bg-yellow-50/50 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => openDetailsModal(appointment)}
        data-testid={`awaiting-card-${appointment.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              <p className="font-semibold">{appointment.client_name}</p>
              <Badge className="bg-yellow-100 text-yellow-800 border-yellow-400">
                <KeyRound className="w-3 h-3 mr-1" />Aguardando Código
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span>{appointment.vehicle_brand} {appointment.vehicle_model}</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs w-fit">{appointment.vehicle_plate}</Badge>
            </div>
          </div>

          <Button
            onClick={(e) => { e.stopPropagation(); openCodeModal(appointment); }}
            className="bg-green-600 hover:bg-green-700 animate-pulse"
            data-testid={`code-btn-${appointment.id}`}
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Digitar Código
          </Button>
        </div>
        
        <Alert className="mt-3 bg-yellow-50 border-yellow-300">
          <KeyRound className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-sm text-yellow-800">
            <strong>Solicite o código de 6 dígitos ao cliente</strong> para concluir o serviço.
          </AlertDescription>
        </Alert>
      </Card>
    );
  };

  const renderCompletedCard = (appointment) => {
    const isPaid = appointment.status === 'PAID';
    const grossPrice = getAppointmentGrossPrice(appointment);
    const netPrice = Number(appointment.provider_amount ?? grossPrice * NET_RATIO) || 0;
    // Estado de contestação (sem alterar o status operacional do serviço)
    const fs = appointment.financial_status;
    const disputeResolution = appointment.dispute_resolution;
    const disputeBadge = fs === 'em_analise'
      ? { label: 'Contestação em análise', cls: 'bg-orange-100 text-orange-700 border-orange-200' }
      : fs === 'revertido' || disputeResolution === 'client_won'
        ? { label: 'Valor revertido', cls: 'bg-red-50 text-red-700 border-red-200' }
        : disputeResolution === 'provider_won'
          ? { label: 'Contestação vencida', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' }
          : null;
    
    return (
      <Card 
        key={appointment.id} 
        className="p-5 border-l-4 border-l-emerald-500 bg-emerald-50/30 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => openDetailsModal(appointment)}
        data-testid={`completed-card-${appointment.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              <p className="font-semibold">{appointment.client_name}</p>
              <Badge className={isPaid ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}>
                <DollarSign className="w-3 h-3 mr-1" />
                {isPaid ? 'Pago ✓' : 'Concluído'}
              </Badge>
              {disputeBadge && (
                <Badge
                  variant="outline"
                  className={`text-[11px] ${disputeBadge.cls}`}
                  data-testid={`dispute-badge-${appointment.id}`}
                >
                  {disputeBadge.label}
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span>{new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
              </div>
              <Badge variant="outline" className="font-mono text-xs w-fit">{appointment.vehicle_plate}</Badge>
            </div>

            {/* Resumo financeiro: bruto vs líquido */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs mt-1" data-testid="completed-financial-summary">
              <span className="text-muted-foreground">
                Valor do serviço: <span className="font-semibold text-slate-700">R$ {grossPrice.toFixed(2)}</span>
              </span>
              <span className="text-muted-foreground">
                Você recebe: <span className="font-semibold text-emerald-700">R$ {netPrice.toFixed(2)}</span>
              </span>
            </div>
            
            {isPaid && (
              <p className="text-xs text-emerald-600 mt-1">
                Repasse concluído
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <CheckCircle2 className={`w-8 h-8 ${isPaid ? 'text-emerald-500' : 'text-blue-500'}`} />
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
        </div>
      </Card>
    );
  };

  const renderHistoryCard = (appointment) => {
    const isCancelled = ['CANCELLED', 'REJECTED'].includes(appointment.status);
    
    return (
      <Card 
        key={appointment.id} 
        className={`p-4 cursor-pointer hover:shadow-md transition-shadow ${isCancelled ? 'border-l-4 border-l-red-400 bg-red-50/30' : 'border-l-4 border-l-gray-300'}`}
        onClick={() => openDetailsModal(appointment)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium">{appointment.client_name}</p>
              <Badge className={getStatusColor(appointment.status)} variant="secondary">
                {getStatusLabel(appointment.status)}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}</span>
              <span>{appointment.time}</span>
              <span className="font-mono">{appointment.vehicle_plate}</span>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>
      </Card>
    );
  };

  const isLoading = loadingPending && loadingServices;

  // Mostrar loading enquanto verifica autenticação
  if (!authChecked || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isLoading && pendingAppointments.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Modal de Código */}
        <Dialog open={codeModalOpen} onOpenChange={setCodeModalOpen}>
            <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Confirmar Código do Cliente
              </DialogTitle>
              <DialogDescription>
                Digite o código de 6 dígitos informado pelo cliente
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <Input
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-3xl font-mono tracking-widest h-16"
                maxLength={6}
                autoFocus
                data-testid="code-input"
              />
              
              <p className="text-sm text-muted-foreground text-center mt-2">
                O cliente visualiza o código no app após a finalização do serviço.
              </p>
            </div>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCodeModalOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmCode} 
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={codeLoading || codeInput.length !== 6}
                data-testid="confirm-code-btn"
              >
                {codeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Confirmar
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de Detalhes do Serviço */}
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Car className="w-5 h-5" />
                Detalhes do Serviço
              </DialogTitle>
            </DialogHeader>
            
            {selectedServiceDetails && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge className={getStatusColor(selectedServiceDetails.status)}>
                    {getStatusLabel(selectedServiceDetails.status)}
                  </Badge>
                </div>
                
                <hr />
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Cliente</p>
                      <p className="font-medium">{selectedServiceDetails.client_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Car className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Veículo</p>
                      <p className="font-medium">
                        {selectedServiceDetails.vehicle_brand || 'N/A'} {selectedServiceDetails.vehicle_model || ''}
                        {selectedServiceDetails.vehicle_plate && (
                          <span className="ml-2 font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">
                            {selectedServiceDetails.vehicle_plate}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Data</p>
                      <p className="font-medium">
                        {new Date(selectedServiceDetails.date + 'T00:00:00').toLocaleDateString('pt-BR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Horário</p>
                      <p className="font-medium">{selectedServiceDetails.time}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Banknote className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Tipo de Serviço</p>
                      <p className="font-medium">{formatServiceType(selectedServiceDetails.service_type)}</p>
                    </div>
                  </div>
                  
                  {/* Valor a Receber - sempre mostra */}
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm text-muted-foreground">Valor que Você Recebe</p>
                      <p className="font-bold text-lg text-emerald-600">
                        R$ {(selectedServiceDetails.provider_amount || (selectedServiceDetails.price ? selectedServiceDetails.price * NET_RATIO : 0)).toFixed(2)}
                      </p>
                      {selectedServiceDetails.price && (
                        <p className="text-xs text-muted-foreground">
                          Valor do serviço: R$ {selectedServiceDetails.price.toFixed(2)} (você recebe {Math.round((1 - platformFeePercent / 100) * 100)}%)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDetailsModalOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* ========== ALERTA DE PIX NÃO CONFIGURADO ========== */}
        {!hasPixConfigured && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-400 border-2">
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
            <AlertDescription className="text-yellow-800 ml-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <strong>⚠️ Cadastre sua chave Pix para solicitar repasses!</strong>
                <p className="text-sm mt-1">
                  Você não poderá solicitar repasses até configurar sua chave Pix.
                  Seu faturamento continua sendo registrado normalmente.
                </p>
              </div>
              <Button 
                size="sm" 
                className="bg-yellow-600 hover:bg-yellow-700 whitespace-nowrap"
                onClick={() => navigate('/provider/settings')}
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Configurações
              </Button>
            </AlertDescription>
          </Alert>
        )}
        
        {/* ========== BLOCO DE RESUMO NO TOPO ========== */}
        <div className="mb-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold">Meus Serviços</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {user?.business_name || user?.name} • Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Botão de som para check-in */}
              <Button 
                variant={soundEnabled ? "default" : "ghost"} 
                size="icon" 
                onClick={() => {
                  // Inicializar áudio na primeira vez
                  if (!audioInitializedRef.current) {
                    initAudioContext();
                    audioInitializedRef.current = true;
                  }
                  setSoundEnabled(!soundEnabled);
                  toast.info(soundEnabled ? '🔇 Som desativado' : '🔔 Som ativado');
                }} 
                title={soundEnabled ? "Som ativado (clique para desativar)" : "Som desativado (clique para ativar)"}
                className={soundEnabled ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                data-testid="toggle-sound-btn"
              >
                <Volume2 className={`w-5 h-5 ${soundEnabled ? 'text-white' : ''}`} />
              </Button>
              
              {/* Botão de configurações */}
              <Button variant="ghost" size="icon" onClick={() => navigate('/provider/settings')} title="Configurações">
                <Settings className="w-5 h-5" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={() => fetchAll()} disabled={loadingPending || loadingServices}>
                <RefreshCw className={`w-4 h-4 mr-2 ${(loadingPending || loadingServices) ? 'animate-spin' : ''}`} />
                Atualizar
              </Button>
            </div>
          </div>

          {/* Cards de Resumo */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-testid="provider-services-summary">
            <Card className="px-4 pt-[14px] pb-[14px] bg-blue-50 border-blue-200 min-h-[140px] flex flex-col justify-between gap-1.5">
              <div className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="text-[13px] font-medium leading-[1.1] text-muted-foreground">Hoje</span>
              </div>
              <div>
                <p className="text-lg font-bold text-blue-700">
                  {todayAppointments.length} serviço{todayAppointments.length !== 1 ? 's' : ''}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5 invisible">spacer</p>
              </div>
            </Card>

            {/* Faturamento Hoje (bruto) */}
            <Card className="px-4 pt-[14px] pb-[14px] bg-emerald-50 border-emerald-200 min-h-[140px] flex flex-col justify-between gap-1.5" data-testid="card-faturamento-hoje">
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <span className="text-[13px] font-medium leading-[1.1] text-muted-foreground">Faturamento Hoje</span>
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-700">
                  R$ {todayEarnings.toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Valor bruto dos serviços</p>
              </div>
            </Card>

            {/* Disponível Hoje (líquido) */}
            <Card className="px-4 pt-[14px] pb-[14px] bg-teal-50 border-teal-200 min-h-[140px] flex flex-col justify-between gap-1.5" data-testid="card-disponivel-hoje">
              <div className="flex items-center gap-1.5">
                <DollarSign className="w-4 h-4 text-teal-600 flex-shrink-0" />
                <span className="text-[13px] font-medium leading-[1.1] text-muted-foreground">Disponível Hoje</span>
              </div>
              <div>
                <p className="text-lg font-bold text-teal-700">
                  R$ {todayNetEarnings.toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Líquido após comissão ({platformFeePercent.toFixed(platformFeePercent % 1 === 0 ? 0 : 1)}%)</p>
              </div>
            </Card>

            {/* Faturamento do Mês (bruto) */}
            <Card className="px-4 pt-[14px] pb-[14px] bg-purple-50 border-purple-200 min-h-[140px] flex flex-col justify-between gap-1.5" data-testid="card-faturamento-mes">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-purple-600 flex-shrink-0" />
                <span className="text-[13px] font-medium leading-[1.1] text-muted-foreground">Faturamento do Mês</span>
              </div>
              <div>
                <p className="text-lg font-bold text-purple-700">
                  R$ {monthlyEarnings.toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Valor bruto dos serviços</p>
              </div>
            </Card>

            {/* Disponível no Mês (líquido) */}
            <Card className="px-4 pt-[14px] pb-[14px] bg-indigo-50 border-indigo-200 min-h-[140px] flex flex-col justify-between gap-1.5" data-testid="card-disponivel-mes">
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                <span className="text-[13px] font-medium leading-[1.1] text-muted-foreground">Disponível no Mês</span>
              </div>
              <div>
                <p className="text-lg font-bold text-indigo-700">
                  R$ {monthlyNetEarnings.toFixed(2)}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Líquido após comissão ({platformFeePercent.toFixed(platformFeePercent % 1 === 0 ? 0 : 1)}%)</p>
              </div>
            </Card>
          </div>

          {/* Próximo serviço */}
          {nextScheduled && (
            <Card className="p-4 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Próximo serviço agendado</p>
                    <p className="font-semibold">
                      {nextScheduled.client_name} • {new Date(nextScheduled.date + 'T00:00:00').toLocaleDateString('pt-BR')} às {nextScheduled.time}
                    </p>
                  </div>
                </div>
                <Badge className={nextScheduled.status === 'ACCEPTED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                  {nextScheduled.status === 'ACCEPTED' ? 'Confirmado' : 'Pendente'}
                </Badge>
              </div>
            </Card>
          )}
        </div>

        {/* ========== SEÇÕES DE SERVIÇOS ========== */}

        {/* SEÇÃO: Aguardando Código (PRIORIDADE) */}
        {awaitingCodeAppointments.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <KeyRound className="w-6 h-6 text-yellow-600" />
              <h2 className="text-xl font-semibold">Aguardando Código do Cliente</h2>
              <Badge className="bg-yellow-500 text-white animate-pulse">
                {awaitingCodeAppointments.length}
              </Badge>
            </div>
            
            <div className="space-y-4">
              {awaitingCodeAppointments.map(renderAwaitingCodeCard)}
            </div>
          </div>
        )}

        {/* SEÇÃO: Em Andamento */}
        {inProgressAppointments.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <h2 className="text-xl font-semibold">Serviços em Andamento</h2>
              <Badge className="bg-blue-500 text-white">{inProgressAppointments.length}</Badge>
            </div>
            <div className="space-y-4">
              {inProgressAppointments.map(renderInProgressCard)}
            </div>
          </div>
        )}

        {/* SEÇÃO: Solicitações Pendentes */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="w-6 h-6 text-yellow-600" />
            <h2 className="text-xl font-semibold">Solicitações de Agendamento</h2>
            {visiblePendingAppointments.length > 0 && (
              <Badge className="bg-yellow-500 text-white">
                {visiblePendingAppointments.length} {visiblePendingAppointments.length === 1 ? 'nova' : 'novas'}
              </Badge>
            )}
          </div>

          {visiblePendingAppointments.length === 0 ? (
            <Card className="p-6 text-center bg-slate-50">
              <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
              <p className="text-muted-foreground font-medium">Nenhuma solicitação pendente.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {visiblePendingAppointments.map(renderPendingCard)}
            </div>
          )}
        </div>

        <hr className="my-8" />

        {/* SEÇÃO: Confirmados (aguardando início) */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
            Serviços Confirmados
            {acceptedAppointments.length > 0 && <Badge variant="outline">{acceptedAppointments.length}</Badge>}
          </h2>

          {acceptedAppointments.length === 0 ? (
            <Card className="p-6 text-center bg-slate-50">
              <Calendar className="w-10 h-10 mx-auto text-slate-400 mb-3" />
              <p className="text-muted-foreground">Nenhum serviço confirmado aguardando início.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {acceptedAppointments.map(renderAcceptedCard)}
            </div>
          )}
        </div>

        {/* Problem 8: a antiga seção independente "Serviços Expirados" foi movida para a aba "Expirados" abaixo. */}

        {/* ========== SEÇÃO: HISTÓRICO E RESUMO FINANCEIRO ========== */}
        <div className="mb-8">
          <Tabs defaultValue="completed" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="completed" className="flex items-center gap-2" data-testid="tab-completed">
                <CheckCircle2 className="w-4 h-4" />
                Concluídos ({completedAppointments.length})
              </TabsTrigger>
              <TabsTrigger value="cancelled" className="flex items-center gap-2" data-testid="tab-cancelled">
                <XCircle className="w-4 h-4" />
                Cancelados ({cancelledAppointments.length})
              </TabsTrigger>
              <TabsTrigger value="expired" className="flex items-center gap-2" data-testid="tab-expired">
                <AlertTriangle className="w-4 h-4" />
                Expirados ({displayExpiredAppointments.length})
              </TabsTrigger>
              <TabsTrigger value="financial" className="flex items-center gap-2" data-testid="tab-financial">
                <DollarSign className="w-4 h-4" />
                Financeiro
              </TabsTrigger>
            </TabsList>

            <TabsContent value="completed">
              {completedAppointments.length === 0 ? (
                <Card className="p-6 text-center bg-slate-50">
                  <History className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                  <p className="text-muted-foreground">Nenhum serviço concluído ainda.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {completedAppointments.slice(0, 10).map(renderCompletedCard)}
                  {completedAppointments.length > 10 && (
                    <p className="text-center text-sm text-muted-foreground">
                      Mostrando os últimos 10 de {completedAppointments.length} serviços
                    </p>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="cancelled">
              {cancelledAppointments.length === 0 ? (
                <Card className="p-6 text-center bg-slate-50">
                  <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-3" />
                  <p className="text-muted-foreground">Nenhum serviço cancelado. Ótimo trabalho!</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {cancelledAppointments.slice(0, 10).map(renderHistoryCard)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="expired">
              {displayExpiredAppointments.length === 0 ? (
                <Card className="p-6 text-center bg-slate-50">
                  <AlertTriangle className="w-10 h-10 mx-auto text-slate-400 mb-3" />
                  <p className="text-muted-foreground">Nenhum serviço expirado.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {displayExpiredAppointments.slice(0, 5).map((appointment) => (
                    <Card 
                      key={appointment.id} 
                      className="p-4 border-l-4 border-l-amber-400 bg-amber-50/30 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => openDetailsModal(appointment)}
                      data-testid={`expired-card-${appointment.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <User className="w-4 h-4 text-slate-600" />
                            <p className="font-medium">{appointment.client_name}</p>
                            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Expirado
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {appointment.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Car className="w-3 h-3" />
                              {appointment.vehicle_brand} {appointment.vehicle_model}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 text-amber-700 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Horário expirado</span>
                        </div>
                      </div>
                    </Card>
                  ))}
                  {displayExpiredAppointments.length > 5 && (
                    <p className="text-center text-sm text-muted-foreground">
                      Mostrando os últimos 5 de {displayExpiredAppointments.length} serviços expirados
                    </p>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="financial">
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    Resumo Financeiro
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Faturamento de Hoje</p>
                        <p className="text-[11px] text-muted-foreground">Valor bruto</p>
                      </div>
                      <span className="text-xl font-bold text-emerald-600">R$ {todayEarnings.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Disponível de Hoje</p>
                        <p className="text-[11px] text-muted-foreground">Líquido após comissão ({platformFeePercent.toFixed(platformFeePercent % 1 === 0 ? 0 : 1)}%)</p>
                      </div>
                      <span className="text-xl font-bold text-teal-600">R$ {todayNetEarnings.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Faturamento do Mês</p>
                        <p className="text-[11px] text-muted-foreground">Valor bruto</p>
                      </div>
                      <span className="text-xl font-bold text-purple-600">R$ {monthlyEarnings.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                      <div>
                        <p className="text-sm text-muted-foreground">Disponível no Mês</p>
                        <p className="text-[11px] text-muted-foreground">Líquido após comissão ({platformFeePercent.toFixed(platformFeePercent % 1 === 0 ? 0 : 1)}%)</p>
                      </div>
                      <span className="text-xl font-bold text-indigo-600">R$ {monthlyNetEarnings.toFixed(2)}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-muted-foreground">Total de Serviços</span>
                      <span className="text-xl font-bold text-blue-600">{completedAppointments.length}</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-blue-600" />
                    Estatísticas
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-muted-foreground">Serviços Hoje</span>
                      <span className="text-xl font-bold">{todayCompleted.length}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                      <span className="text-muted-foreground">Serviços este Mês</span>
                      <span className="text-xl font-bold">{monthlyCompleted.length}</span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <span className="text-muted-foreground">Cancelados</span>
                      <span className="text-xl font-bold text-red-600">{cancelledAppointments.length}</span>
                    </div>
                  </div>
                </Card>
              </div>
              
              <p className="text-xs text-muted-foreground text-center mt-4">
                * Valores baseados nos serviços registrados. Integração com Stripe será ativada em produção.
              </p>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
